import React, { useState, useRef, useEffect } from 'react';
import { X, Download, Send, Edit2, Save, FileText, DollarSign } from 'lucide-react';
import SignatureCanvas from 'react-signature-canvas';
import { toast } from 'sonner';
import { format } from 'date-fns';

const DocumentEditor = ({ template, cases, referrals, onClose }) => {
  const [documentContent, setDocumentContent] = useState('');
  const [selectedCase, setSelectedCase] = useState(null);
  const [editableFields, setEditableFields] = useState({
    fee_percentage: '35',
    client_percentage: '65',
    settlement_percentage: '85',
    retained_percentage: '15',
    recovery_percentage: '80',
    release_percentage: '75',
    retention_days: '90'
  });
  const [showSignature, setShowSignature] = useState(false);
  const [signature, setSignature] = useState(null);
  const sigCanvas = useRef(null);
  const [draggedItem, setDraggedItem] = useState(null);

  useEffect(() => {
    // Initialize with template content, replacing placeholders with safe defaults
    let safeContent = template.content;
    const placeholders = [
      'current_date', 'client_name', 'company_name', 'client_email', 'client_phone',
      'processor_name', 'frozen_amount', 'freeze_date', 'days_frozen', 'bank_name',
      'bank_address', 'bank_phone', 'case_id', 'case_status', 'fee_percentage',
      'client_percentage', 'settlement_percentage', 'settlement_amount', 'retained_percentage',
      'retained_amount', 'recovery_percentage', 'estimated_recovery', 'release_percentage',
      'release_amount', 'retention_days', 'demand_date', 'estimated_timeline'
    ];
    
    placeholders.forEach(placeholder => {
      const regex = new RegExp(`\\{\\{${placeholder}\\}\\}`, 'g');
      safeContent = safeContent.replace(regex, `[${placeholder.replace(/_/g, ' ').toUpperCase()}]`);
    });
    
    setDocumentContent(safeContent);
  }, [template]);

  const handleDragStart = (item, type) => {
    setDraggedItem({ ...item, itemType: type });
  };

  const handleDrop = (e) => {
    e.preventDefault();
    if (draggedItem) {
      populateTemplate(draggedItem);
      setSelectedCase(draggedItem);
      setDraggedItem(null);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const populateTemplate = (item) => {
    let content = template.content;
    const today = format(new Date(), 'MMMM dd, yyyy');
    const freezeDate = item.freeze_date ? format(new Date(item.freeze_date), 'MMMM dd, yyyy') : 'N/A';
    const daysFrozen = item.freeze_date
      ? Math.floor((new Date() - new Date(item.freeze_date)) / (1000 * 60 * 60 * 24))
      : 0;

    // Calculate derived values
    const frozenAmount = item.frozen_amount || item.held_reserves_amount || 0;
    const feePercentage = parseFloat(editableFields.fee_percentage);
    const settlementPercentage = parseFloat(editableFields.settlement_percentage);
    const recoveryPercentage = parseFloat(editableFields.recovery_percentage);
    const releasePercentage = parseFloat(editableFields.release_percentage);

    const settlementAmount = (frozenAmount * settlementPercentage / 100).toFixed(2);
    const retainedAmount = (frozenAmount * parseFloat(editableFields.retained_percentage) / 100).toFixed(2);
    const estimatedRecovery = (frozenAmount * recoveryPercentage / 100).toFixed(2);
    const releaseAmount = (frozenAmount * releasePercentage / 100).toFixed(2);

    // Replace placeholders
    const replacements = {
      '{{current_date}}': today,
      '{{client_name}}': item.client_name || item.merchant_contact_name || 'Client Name',
      '{{company_name}}': item.company_name || item.merchant_company_name || 'Company Name',
      '{{client_email}}': item.client_email || item.merchant_contact_email || 'client@example.com',
      '{{client_phone}}': item.client_phone || item.merchant_contact_phone || 'N/A',
      '{{processor_name}}': item.processor_name || 'Processor Name',
      '{{frozen_amount}}': frozenAmount.toLocaleString(),
      '{{freeze_date}}': freezeDate,
      '{{days_frozen}}': daysFrozen,
      '{{bank_name}}': item.merchant_bank_name || 'Bank Name',
      '{{bank_address}}': item.merchant_bank_address || 'Bank Address',
      '{{bank_phone}}': item.merchant_bank_phone || 'Bank Phone',
      '{{case_id}}': item.id ? item.id.substring(0, 8) : 'N/A',
      '{{case_status}}': (item.status || 'pending').toUpperCase(),
      '{{fee_percentage}}': editableFields.fee_percentage,
      '{{client_percentage}}': editableFields.client_percentage,
      '{{settlement_percentage}}': editableFields.settlement_percentage,
      '{{settlement_amount}}': settlementAmount,
      '{{retained_percentage}}': editableFields.retained_percentage,
      '{{retained_amount}}': retainedAmount,
      '{{recovery_percentage}}': editableFields.recovery_percentage,
      '{{estimated_recovery}}': estimatedRecovery,
      '{{release_percentage}}': editableFields.release_percentage,
      '{{release_amount}}': releaseAmount,
      '{{retention_days}}': editableFields.retention_days,
      '{{demand_date}}': format(new Date(Date.now() - 7 * 24 * 60 * 60 * 1000), 'MMMM dd, yyyy'),
      '{{estimated_timeline}}': '60-90',
      // POA specific placeholders
      '{{merchant_legal_name}}': item.company_name || item.merchant_company_name || 'Merchant Legal Name',
      '{{merchant_address}}': item.business_address || item.address || 'Business Address',
      '{{mid_numbers}}': item.mid_number || item.merchant_id || 'N/A',
      '{{merchant_name}}': item.company_name || item.merchant_company_name || 'Merchant Name',
      '{{merchant_contact_name}}': item.client_name || item.merchant_contact_name || 'Contact Name',
      '{{merchant_contact_email}}': item.client_email || item.merchant_contact_email || 'contact@example.com',
      '{{merchant_contact_phone}}': item.client_phone || item.merchant_contact_phone || 'N/A',
      '{{held_reserves_amount}}': frozenAmount.toLocaleString()
    };

    Object.entries(replacements).forEach(([placeholder, value]) => {
      content = content.split(placeholder).join(value);
    });

    setDocumentContent(content);
    toast.success('Template populated with case data!');
  };

  const handleFieldChange = (field, value) => {
    setEditableFields(prev => ({ ...prev, [field]: value }));
    if (selectedCase) {
      populateTemplate(selectedCase);
    }
  };

  const clearSignature = () => {
    sigCanvas.current.clear();
    setSignature(null);
  };

  const saveSignature = () => {
    if (sigCanvas.current.isEmpty()) {
      toast.error('Please provide a signature first');
      return;
    }
    const signatureData = sigCanvas.current.toDataURL();
    setSignature(signatureData);
    setShowSignature(false);
    toast.success('Signature captured!');
  };

  const handleDownload = () => {
    // Create a printable HTML document
    const printContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>${template.name}</title>
          <style>
            body {
              font-family: 'Times New Roman', serif;
              max-width: 8.5in;
              margin: 0 auto;
              padding: 1in;
              line-height: 1.6;
              color: #000;
            }
            h1, h2, h3 { color: #000; }
            h1 { font-size: 24px; margin-bottom: 10px; }
            h2 { font-size: 20px; margin-top: 20px; }
            h3 { font-size: 16px; margin-top: 15px; }
            p { margin: 10px 0; }
            ul, ol { margin: 10px 0; padding-left: 30px; }
            .header { text-align: center; margin-bottom: 30px; }
            .signature-line { margin-top: 60px; border-top: 2px solid #000; padding-top: 10px; }
            @media print {
              body { margin: 0; padding: 0.5in; }
            }
          </style>
        </head>
        <body>
          ${documentContent}
          ${signature ? `<div style="margin-top: 30px;"><p><strong>Client E-Signature:</strong></p><img src="${signature}" style="max-width: 300px; border: 1px solid #ccc; padding: 10px;" /></div>` : ''}
        </body>
      </html>
    `;

    const blob = new Blob([printContent], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${template.id}-${Date.now()}.html`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Document downloaded!');
  };

  return (
    <div className="flex gap-6 h-[calc(100vh-200px)]">
      {/* Left Sidebar - Cases & Referrals */}
      <div className="w-80 bg-white border border-slate-200 rounded-lg p-4 overflow-y-auto">
        <h3 className="font-bold text-slate-900 mb-4">Drag Data to Document</h3>
        
        {/* Cases */}
        <div className="mb-6">
          <h4 className="text-sm font-semibold text-slate-700 mb-2">Cases ({cases.length})</h4>
          <div className="space-y-2">
            {cases.map((caseItem) => (
              <div
                key={caseItem.id}
                draggable
                onDragStart={() => handleDragStart(caseItem, 'case')}
                className="p-3 bg-slate-50 border border-slate-200 rounded-lg cursor-move hover:border-purple-400 hover:shadow-md transition-all"
              >
                <div className="flex items-start gap-2">
                  <FileText className="text-purple-600 flex-shrink-0" size={16} />
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm text-slate-900 truncate">{caseItem.company_name}</p>
                    <p className="text-xs text-slate-600">{caseItem.client_name}</p>
                    <p className="text-xs font-semibold text-purple-600">${caseItem.frozen_amount?.toLocaleString()}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Referrals */}
        <div>
          <h4 className="text-sm font-semibold text-slate-700 mb-2">Referrals ({referrals.length})</h4>
          <div className="space-y-2">
            {referrals.map((referral) => (
              <div
                key={referral.id}
                draggable
                onDragStart={() => handleDragStart(referral, 'referral')}
                className="p-3 bg-cyan-50 border border-cyan-200 rounded-lg cursor-move hover:border-cyan-400 hover:shadow-md transition-all"
              >
                <div className="flex items-start gap-2">
                  <DollarSign className="text-cyan-600 flex-shrink-0" size={16} />
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm text-slate-900 truncate">{referral.merchant_company_name}</p>
                    <p className="text-xs text-slate-600">{referral.merchant_contact_name}</p>
                    <p className="text-xs font-semibold text-cyan-600">${referral.held_reserves_amount?.toLocaleString()}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Main Document Editor */}
      <div className="flex-1 flex flex-col bg-white border border-slate-200 rounded-lg">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-200">
          <div>
            <h2 className="text-xl font-bold text-slate-900">{template.name}</h2>
            <p className="text-sm text-slate-600">Drag a case/referral to auto-fill the document</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowSignature(true)}
              className="px-4 py-2 border border-slate-300 rounded-lg text-slate-700 hover:bg-slate-50 flex items-center gap-2"
            >
              <Edit2 size={16} />
              {signature ? 'Update Signature' : 'Add Signature'}
            </button>
            <button
              onClick={handleDownload}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center gap-2"
            >
              <Download size={16} />
              Download
            </button>
            <button
              onClick={onClose}
              className="p-2 text-slate-400 hover:text-slate-600"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Editable Fields Panel */}
        <div className="p-4 bg-slate-50 border-b border-slate-200">
          <h3 className="text-sm font-semibold text-slate-900 mb-3">Editable Fields</h3>
          <div className="grid grid-cols-4 gap-3">
            <div>
              <label className="text-xs text-slate-600">Fee %</label>
              <input
                type="number"
                value={editableFields.fee_percentage}
                onChange={(e) => handleFieldChange('fee_percentage', e.target.value)}
                className="w-full px-2 py-1 text-sm border border-slate-200 rounded focus:ring-2 focus:ring-purple-500 outline-none"
              />
            </div>
            <div>
              <label className="text-xs text-slate-600">Client %</label>
              <input
                type="number"
                value={editableFields.client_percentage}
                onChange={(e) => handleFieldChange('client_percentage', e.target.value)}
                className="w-full px-2 py-1 text-sm border border-slate-200 rounded focus:ring-2 focus:ring-purple-500 outline-none"
              />
            </div>
            <div>
              <label className="text-xs text-slate-600">Settlement %</label>
              <input
                type="number"
                value={editableFields.settlement_percentage}
                onChange={(e) => handleFieldChange('settlement_percentage', e.target.value)}
                className="w-full px-2 py-1 text-sm border border-slate-200 rounded focus:ring-2 focus:ring-purple-500 outline-none"
              />
            </div>
            <div>
              <label className="text-xs text-slate-600">Recovery %</label>
              <input
                type="number"
                value={editableFields.recovery_percentage}
                onChange={(e) => handleFieldChange('recovery_percentage', e.target.value)}
                className="w-full px-2 py-1 text-sm border border-slate-200 rounded focus:ring-2 focus:ring-purple-500 outline-none"
              />
            </div>
          </div>
        </div>

        {/* Document Content */}
        <div
          className="flex-1 p-8 overflow-y-auto bg-white"
          onDrop={handleDrop}
          onDragOver={handleDragOver}
        >
          {!selectedCase ? (
            <div className="h-full flex items-center justify-center text-center">
              <div>
                <FileText className="mx-auto mb-4 text-slate-300" size={64} />
                <p className="text-slate-500 mb-2">Drag a case or referral here to populate the template</p>
                <p className="text-sm text-slate-400">The document will auto-fill with the selected data</p>
              </div>
            </div>
          ) : (
            <div
              className="document-content"
              style={{
                fontFamily: "'Times New Roman', serif",
                fontSize: '14px',
                lineHeight: '1.6',
                maxWidth: '8.5in',
                margin: '0 auto'
              }}
              dangerouslySetInnerHTML={{ __html: documentContent }}
            />
          )}
        </div>

        {/* Signature Display */}
        {signature && (
          <div className="p-4 border-t border-slate-200 bg-slate-50">
            <h4 className="text-sm font-semibold text-slate-900 mb-2">Client E-Signature</h4>
            <img src={signature} alt="Signature" className="max-w-xs border border-slate-300 rounded p-2 bg-white" />
          </div>
        )}
      </div>

      {/* Signature Modal */}
      {showSignature && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-slate-900">Client E-Signature</h3>
              <button onClick={() => setShowSignature(false)} className="text-slate-400 hover:text-slate-600">
                <X size={24} />
              </button>
            </div>
            <p className="text-sm text-slate-600 mb-4">Draw your signature below to approve this document</p>
            <div className="border-2 border-slate-300 rounded-lg mb-4 bg-white">
              <SignatureCanvas
                ref={sigCanvas}
                canvasProps={{
                  className: 'w-full h-64',
                  style: { touchAction: 'none' }
                }}
              />
            </div>
            <div className="flex gap-3">
              <button
                onClick={clearSignature}
                className="flex-1 px-4 py-2 border border-slate-300 rounded-lg text-slate-700 hover:bg-slate-50"
              >
                Clear
              </button>
              <button
                onClick={saveSignature}
                className="flex-1 primary-button py-2"
              >
                Save Signature
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentEditor;
